import * as THREE from "three";
import { FIRE, PALETTE, VFX, type Rgb, type VfxLightSlot } from "../../../../../art/vfx.js";
import { type BreathSpec, coneRadius, launchSpeed } from "./companionFlight";

/**
 * The Dragonlord drake's breath of fire (owner 2026-10-20: "the dragon needs work, as does its flame breath"; it was a
 * few `fx.flames` puffs that read as nothing at phone zoom). A continuous, turbulent stream thrown from between the
 * drake's lips, ahead and down onto the ground as it swoops past, built from the pooled VFX particles (no new draw
 * call, texture or material: the atlas `flame`, `explosion`, `smoke` and `glow` frames, the fire on the hot ramp):
 *
 *   core     a yellow-white glow held AT the lips (it rides with the drake), so the fire visibly leaves the mouth
 *   tongues  the body: flame cells turned on screen to lie down the stream, widening into a cone
 *            (companionFlight.coneRadius), orange cooling to red tips
 *   billows  normal-blended orange fireballs: a solid body that keeps its colour where additive fire would sum to white
 *   soot     dark, normal-blended smoke fading in toward the far end, and rolling up off the landing point
 *   embers   glowing motes flung along and out of the stream, falling
 *   ground   where it lands: a flash and a ring on first contact, a scorch, flames licking up, sparks
 *   light    one VFX light slot (render/vfxLights.ts: six shared uniforms, not a THREE.PointLight) riding the stream
 *
 * No lag at the mouth: the level updates the views before the VFX, which then moves every live particle one frame on
 * before drawing it — so each puff is spawned one frame's travel BEHIND the lips (`launch`), and the first drawn frame of
 * the stream starts at them. Camera-facing sprites, rotated down the stream (written when the VFX's velocity-aligned
 * quads collapsed for a velocity along the camera's up; art/vfx.js fixed that on 2026-10-20, test/vfxAlign.test.ts).
 *
 * `frame` is called every frame with the stream's strength (companionFlight.breathLevel: the breath clip's jaw-open
 * window). The particle specs are reused objects (VFX.spawn copies them), so a frame allocates nothing.
 */

/** The stream's look (world units, seconds, particles a second at full strength). */
export const BREATH_FX = {
  /** Seconds a tongue takes to reach the landing point, how much of the drake's own velocity the fire keeps, its drag. */
  travel: 0.55, inherit: 0.4, drag: 1.0,
  /** The steepest and shallowest the stream may fall from the lips (rad below level). */
  fall: [0.3, 0.75],
  /** The quality tier's burst scale is floored here: the breath is a rare hero moment and thins to puffs below it. */
  minLod: 0.85,
  tongues: { rate: 110, life: 0.75, heat: [0.55, 0.8], aspect: 2.2, bright: 0.7, jitter: 0.03, wobble: 0.25 },
  core: { rate: 60, life: 0.09, size: [0.16, 0.3], push: 0.6, bright: 1.6 },
  billows: { rate: 46, life: 0.8, heat: 0.66, size: [0.2, 1.15], fadeIn: 0.06, bright: 1.0 },
  soot: { rate: 34, life: 1.8, size: [0.5, 2.4], alpha: 0.85, fadeIn: 0.28, lift: 0.9, push: 0.75 },
  /** Soot rolling up off where the stream lands (a second, upright plume), per second. */
  plume: { rate: 14, rise: 0.9, spread: 0.35 },
  embers: { rate: 40, life: 0.9, size: 0.09, gravity: 2.6, spread: 1.6 },
  ground: { lick: 0.08, lickSize: 0.4, lickLife: 0.5, sparkEvery: 0.18, scorch: 1.6, ring: 1.8, flash: 0.8 },
  light: { radius: 3.4, intensity: 2.4, lift: 0.35, tail: 0.35 },
  /** Tints over the hot ramp: the tongues start orange (not the ramp's cream) and cool to red tips. */
  warm: [1, 0.58, 0.26] as Rgb,
  cool: [0.85, 0.24, 0.08] as Rgb,
  coreColour: [1, 0.82, 0.42] as Rgb,
  sootColour: [0.07, 0.06, 0.055] as Rgb,
} as const;

/** The VFX particle spawn (art/vfx.js `spawn`: copies the spec into a pooled particle and returns it); not in vfx.d.ts. */
interface Spawner { spawn(spec: Record<string, unknown>): { pos: THREE.Vector3 } }

/** The particle layers, in spawn order. */
type Layer = "tongues" | "core" | "billows" | "soot" | "embers";
const LAYERS: readonly Layer[] = ["tongues", "core", "billows", "soot", "embers"];

const WHITE: Rgb = [1, 1, 1];
const growOut = (t: number): number => 1 - (1 - t) * (1 - t);
/** A puff's swell: most of its size in its first third, so the stream is full-width all the way down. */
const swell = (t: number): number => 1 - Math.pow(1 - t, 4);
const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

const _d = new THREE.Vector3();
const _s = new THREE.Vector3();
const _u = new THREE.Vector3();
const _p = new THREE.Vector3();
const _v = new THREE.Vector3();
const _c = new THREE.Vector3();
const _q = new THREE.Quaternion();

export class BreathStream {
  private readonly owed = { tongues: 0, core: 0, billows: 0, soot: 0, embers: 0, lick: 0, spark: 0, plume: 0 };
  private on = false;
  /** Seconds the stream has been flowing (the ground effects wait for the first tongues to land). */
  private flowing = 0;
  private landed = false;
  private light: VfxLightSlot | null = null;
  /** The stream's direction on screen (rad, a sprite's `rot` that lays a flame cell's tip down the stream). */
  private screenAngle = 0;
  private readonly land = new THREE.Vector3();
  /** Review tooling: the lips this frame and the last core and tongue puffs spawned (live VFX particles). */
  readonly probe: { mouth: THREE.Vector3; core: { pos: THREE.Vector3 } | null; tongue: { pos: THREE.Vector3 } | null } = { mouth: new THREE.Vector3(), core: null, tongue: null };
  // reused spawn specs (VFX.spawn copies the fields it reads)
  private readonly tongue: Record<string, unknown>;
  private readonly core: Record<string, unknown>;
  private readonly billow: Record<string, unknown>;
  private readonly soot: Record<string, unknown>;
  private readonly ember: Record<string, unknown>;

  constructor(private readonly b: BreathSpec) {
    const F = BREATH_FX;
    this.tongue = { pos: _p, vel: _v, anim: "flame", animRate: FIRE.flicker, aspect: F.tongues.aspect,
      drag: F.drag, life: F.tongues.life, fadeIn: 0, fadeOut: 0.5, color0: F.warm, color1: F.cool, bright: F.tongues.bright, sizeEase: swell };
    this.core = { pos: _p, vel: _v, frame: "glow", life: F.core.life, size0: F.core.size[0], size1: F.core.size[1], fadeIn: 0, fadeOut: 0.6,
      color0: F.coreColour, color1: PALETTE.fire.core, bright: F.core.bright };
    this.billow = { pos: _p, vel: _v, anim: "explosion", drag: F.drag, life: F.billows.life, sizeEase: swell, fadeIn: F.billows.fadeIn,
      fadeOut: 0.5, color0: WHITE, color1: F.cool, bright: F.billows.bright, additive: false };
    this.soot = { pos: _p, vel: _v, anim: "smoke", drag: F.drag * 1.4, gravity: -F.soot.lift, life: F.soot.life, size0: F.soot.size[0],
      size1: F.soot.size[1], sizeEase: growOut, color0: F.sootColour, alpha: F.soot.alpha, fadeIn: F.soot.fadeIn, fadeOut: 0.45, additive: false };
    this.ember = { pos: _p, vel: _v, frame: "glow", gravity: F.embers.gravity, drag: 1, life: F.embers.life, size0: F.embers.size,
      size1: F.embers.size * 0.3, color0: PALETTE.fire.core, color1: PALETTE.fire.rim, bright: 1.8, fadeIn: 0, fadeOut: 0.5 };
  }

  /**
   * One frame of the stream: `level` 0..1 (0 stops it; the live fire burns out on its own), from the lips `mouth`,
   * leaving along a blend of the `snout`'s direction (unit) and the line to `target` (the ground ahead of the drake), the
   * drake moving at `vel` (world units/s), `floor` the ground's height there.
   */
  frame(dt: number, fx: VFX, level: number, mouth: THREE.Vector3, snout: THREE.Vector3, target: THREE.Vector3, vel: THREE.Vector3, floor: number): void {
    if (level <= 0) { if (this.on) this.stop(); return; }
    const F = BREATH_FX, b = this.b;
    if (!this.aim(mouth, snout, target, floor)) return;
    const reach = this.land.distanceTo(mouth);
    const speed = launchSpeed(reach, F.drag, F.travel);
    const spread = (coneRadius(b, 1) / reach) * speed;            // the side speed that puts a tongue at the cone's rim
    const k = level * Math.max(F.minLod, fx.lod);
    fx.camera.getWorldQuaternion(_q);
    this.screenAngle = Math.atan2(_d.dot(_c.set(0, 1, 0).applyQuaternion(_q)), _d.dot(_c.set(1, 0, 0).applyQuaternion(_q))) + Math.PI / 2;
    if (!this.on) this.begin(fx);
    this.flowing += dt;
    this.probe.mouth.copy(mouth);
    const S = fx as unknown as Spawner;
    for (const layer of LAYERS) {
      this.owed[layer] += F[layer].rate * k * dt;
      while (this.owed[layer] >= 1) {
        this.owed[layer] -= 1;
        const p = S.spawn(this.spec(layer, fx, mouth, speed, spread, vel, floor, dt));
        if (layer === "core") this.probe.core = p; else if (layer === "tongues") this.probe.tongue = p;
      }
    }
    this.ground(dt, fx, k, floor);
    if (this.light) {
      this.light.x = (mouth.x + this.land.x) / 2; this.light.y = (mouth.y + this.land.y) / 2 + F.light.lift; this.light.z = (mouth.z + this.land.z) / 2;
    }
  }

  /** Where the stream lands now (world; valid while it flows). */
  get landing(): THREE.Vector3 { return this.land; }

  /** The stream's direction (`_d`, its cross axes `_s` `_u`) and landing point; false when there is nowhere to go. */
  private aim(mouth: THREE.Vector3, snout: THREE.Vector3, target: THREE.Vector3, floor: number): boolean {
    const F = BREATH_FX, b = this.b;
    _d.subVectors(target, mouth);
    if (_d.lengthSq() < 1e-6) return false;
    _d.normalize().multiplyScalar(1 - b.snout).addScaledVector(snout, b.snout);
    // held between the shallowest and steepest fall: a long diagonal, never a level jet nor a vertical blast
    const h = Math.hypot(_d.x, _d.z);
    if (h < 1e-6) return false;
    const fall = Math.max(F.fall[0], Math.min(F.fall[1], Math.atan2(-_d.y, h)));
    _d.set((_d.x / h) * Math.cos(fall), -Math.sin(fall), (_d.z / h) * Math.cos(fall));
    const drop = mouth.y - floor;
    const reach = Math.min(b.reach, drop > 0 ? drop / Math.sin(fall) : b.reach);
    this.land.copy(mouth).addScaledVector(_d, reach);
    _s.crossVectors(_d, THREE.Object3D.DEFAULT_UP).normalize();
    _u.crossVectors(_s, _d).normalize();
    return true;
  }

  private begin(fx: VFX): void {
    const F = BREATH_FX;
    this.on = true; this.flowing = 0; this.landed = false;
    // the light lives for the whole jaw-open window plus the fire's own life (the window in real seconds: clip ÷ rate)
    const life = (this.b.open[1] - this.b.open[0]) / this.b.rate + F.light.tail;
    this.light = fx.light(this.land, PALETTE.fire.rim, F.light.radius, F.light.intensity, life, { flicker: true });
  }

  private stop(): void {
    this.on = false; this.light = null;
    const o = this.owed;
    o.tongues = o.core = o.billows = o.soot = o.embers = o.lick = o.spark = o.plume = 0;
  }

  /** The landing point: the first contact's flash and ring, the scorch, flames licking up, sparks, a plume of soot. */
  private ground(dt: number, fx: VFX, k: number, floor: number): void {
    const F = BREATH_FX, G = F.ground, at = this.land;
    if (this.flowing < F.travel || at.y > floor + coneRadius(this.b, 1)) return;   // not down yet, or short in the air
    _p.set(at.x, floor, at.z);
    if (!this.landed) {
      this.landed = true;
      fx.flash(_p, G.flash, PALETTE.fire.rim);
      fx.ring(_p, { size: G.ring, life: 0.45, color: PALETTE.fire.rim, bright: 1.4 });
      fx.decal(_p, { type: "scorch", size: G.scorch, life: 4.5, hold: 2.2, color: [0.09, 0.07, 0.06], bright: 1.3 });
    }
    const r = coneRadius(this.b, 1);
    this.owed.lick += dt * k;
    while (this.owed.lick >= G.lick) {
      this.owed.lick -= G.lick;
      fx.flames(_s.set(at.x + rnd(-r, r), floor, at.z + rnd(-r, r)), { size: G.lickSize * rnd(0.7, 1.2), life: G.lickLife });
    }
    this.owed.spark += dt * k;
    if (this.owed.spark >= G.sparkEvery) { this.owed.spark = 0; fx.sparks(_p, 4, { color: PALETTE.fire.rim, speed: 2.2, up: 1.6, gravity: 6, life: 0.45, size: 0.09, floor }); }
    this.owed.plume += F.plume.rate * k * dt;
    while (this.owed.plume >= 1) {
      this.owed.plume -= 1;
      _p.set(at.x + rnd(-r, r), floor + 0.15, at.z + rnd(-r, r));
      _v.set(rnd(-1, 1) * F.plume.spread, F.plume.rise, rnd(-1, 1) * F.plume.spread);
      this.soot.rot = rnd(0, Math.PI * 2); this.soot.rotVel = rnd(-2, 2); this.soot.floor = floor;
      (fx as unknown as Spawner).spawn(this.soot);
    }
  }

  /**
   * Sets the shared spawn position and velocity: along the stream at `speed`, a random side speed within `spread`, plus
   * `inherit` of the drake's. The puff starts a random part of one frame's travel behind the lips, so after the VFX
   * moves it on this frame it is drawn between the lips and one frame down the stream: no gap at the mouth.
   */
  private launch(mouth: THREE.Vector3, speed: number, spread: number, vel: THREE.Vector3, inherit: number, jitter: number, dt: number): void {
    const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * spread;
    _v.copy(_d).multiplyScalar(speed).addScaledVector(_s, Math.cos(a) * r).addScaledVector(_u, Math.sin(a) * r).addScaledVector(vel, inherit);
    _p.copy(mouth).addScaledVector(_v, -dt * Math.random());
    if (jitter > 0) _p.x += rnd(-jitter, jitter), _p.y += rnd(-jitter, jitter), _p.z += rnd(-jitter, jitter);
  }

  /** One particle of `layer`: its reused spec, aimed and sized for this spawn. */
  private spec(layer: Layer, fx: VFX, mouth: THREE.Vector3, speed: number, spread: number, vel: THREE.Vector3, floor: number, dt: number): Record<string, unknown> {
    const F = BREATH_FX, b = this.b, j = F.tongues.jitter, inh = F.inherit;
    switch (layer) {
      case "tongues": {
        this.launch(mouth, speed * rnd(0.9, 1.1), spread, vel, inh, j, dt);
        const t = this.tongue;
        t.size0 = coneRadius(b, 0) * 2 * rnd(0.8, 1.2); t.size1 = coneRadius(b, 1) * 2 * rnd(0.7, 1.1);
        t.ramp = fx.hot(rnd(F.tongues.heat[0], F.tongues.heat[1])) ?? "fire"; t.floor = floor;
        t.rot = this.screenAngle + rnd(-F.tongues.wobble, F.tongues.wobble);
        return t;
      }
      case "core":
        // carried with the drake (all of its velocity), barely pushed out: it stays on the lips while it lives
        this.launch(mouth, speed * F.core.push * Math.random(), 0, vel, 1, 0, dt);
        _p.copy(mouth).addScaledVector(_v, -dt);
        return this.core;
      case "billows": {
        this.launch(mouth, speed * rnd(0.85, 1.05), spread * 1.2, vel, inh, j, dt);
        const t = this.billow;
        t.size0 = F.billows.size[0]; t.size1 = F.billows.size[1] * rnd(0.8, 1.2) * coneRadius(b, 1) * 2;
        t.ramp = fx.hot(F.billows.heat) ?? "fire"; t.rot = rnd(0, Math.PI * 2); t.rotVel = rnd(-3, 3); t.floor = floor;
        return t;
      }
      case "soot":
        this.launch(mouth, speed * F.soot.push, spread * 1.5, vel, inh, j, dt);
        this.soot.rot = rnd(0, Math.PI * 2); this.soot.rotVel = rnd(-2.2, 2.2); this.soot.floor = floor + 0.1;
        return this.soot;
      case "embers":
        this.launch(mouth, speed * rnd(0.5, 1), spread * F.embers.spread, vel, inh, j, dt);
        this.ember.floor = floor;
        return this.ember;
    }
  }
}
